import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../../base.component';
import { Observable, distinctUntilChanged, expand, filter, map, of, takeUntil, tap } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { CustomAiPromptComponent } from '../../dialogs/custom-ai-prompt/custom-ai-prompt.component';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { UntypedFormControl } from '@angular/forms';
import { UserConfigsService } from '../../../services/user-configs.service';
import { Prompt } from '../../../models/prompt.model';
import { SearchParameter } from '../../../models/review/search-parameter.model';
import { SelectionService } from '../../../models/review/selection-service';
import { DocService } from '@services/doc.service';
import { AiAssistantStatus, AiAssistantStore, AiEngineModels } from '@store/component/ai-assistant.store';
import { ThirdPartySettings } from '@store/third-party/third-party.state';
import { Store } from '@ngrx/store';
import { currentOpenAiSettings } from '@store/third-party/open-ai-settings/open-ai-settings.selector';
import { currentPrompts } from '@store/ai-insights/prompts/prompts.selector';
import { ThirdPartyId } from '@models/third-party-id.enum';
import { Role } from '@models/role.model';


@Component({
	selector: 'lex-ai-multi-doc-prompt',
	templateUrl: './ai-multi-doc-prompt.component.html',
	styleUrls: ['./ai-multi-doc-prompt.component.scss'],
	standalone: false
})
export class AiMultiDocPromptComponent extends BaseComponent implements OnInit, OnDestroy {

	@ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
	@ViewChild('scrollResult') private scrollContainer: ElementRef;

	public status$: Observable<AiAssistantStatus> = this.aiAssistantStore.currentStatus$;
	public response$: Observable<string[]> = this.aiAssistantStore.currentResponse$;
	public localPrompts: Prompt[];

	public isLoading: boolean = false;
	public isResultLoading: boolean = false;
	public showContinue: boolean = false;
	public showRetry: boolean = false;

	public showPromptControl: boolean = true;

	public selectedAiEngineModel: number;
	public selectedPrompt: Prompt = null;
	public promptControl = new UntypedFormControl('');
	public hoverPromptId: string = '';

	public showTokenWarning: boolean = false;
	public showTokenError: boolean = false;
	public currentUserId = '';

	public mode: string = 'indeterminate';

	public aiEngines = [];

	private selectedDocIds: string[] = [];
	private currentDocIds: string[] = [];
	private currentWorkflowKey: string = '';

	private currentModel: string;

	private searchParameter: SearchParameter = null;
	private expandEmailFamilySelected: boolean = false;
	private sortBy: string = '';

	private openAiSettings$: Observable<ThirdPartySettings> = this.store.select(currentOpenAiSettings());
	private globalPrompts$: Observable<Prompt[]> = this.store.select(currentPrompts());
	private globalPrompts: Prompt[];

	private shouldAutoScroll: boolean = true;

	constructor(
		public userConfigsService: UserConfigsService,
		private store: Store,
		private aiAssistantStore: AiAssistantStore,
		private dialog: MatDialog,
		private selectionService: SelectionService,
		private docService: DocService,
		private dialogRef: MatDialogRef<AiMultiDocPromptComponent>,
		@Inject(MAT_DIALOG_DATA) data) {
		super();

		if (data.searchParameter) this.searchParameter = data.searchParameter;
		this.expandEmailFamilySelected = data.expandEmailFamilySelected;
		this.sortBy = data.sortBy;

		this.currentUserId = this.userConfigsService?.currentConfig?.userId;
		this.openAiSettings$.pipe(
			tap((settings) => {
				this.currentModel = settings.model
				if (settings.isLinked) {
					if (this.userConfigsService.inRoles(Role.Employee))
						this.aiEngines = AiEngineModels;
					else
						this.aiEngines = AiEngineModels.filter(x => x.id === ThirdPartyId.OpenAi);
					this.selectedAiEngineModel = 0;
					this.aiAssistantStore.setExternalAppId(this.selectedAiEngineModel);
				}
				else if (this.userConfigsService.inRoles(Role.Employee)) {
					this.aiEngines = AiEngineModels.filter(x => x.id === ThirdPartyId.AmazonBedrockClaude);
					this.selectedAiEngineModel = 2;
					this.aiAssistantStore.setExternalAppId(this.selectedAiEngineModel);
				}
				else {
					this.aiEngines = [];
					this.selectedAiEngineModel = -1;
				}
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.globalPrompts$.pipe(
			filter((prompts) => !!prompts),
			map((prompts) => {
				this.aiAssistantStore.addPrompts(prompts)
				return prompts
			}),
			tap((prompts) => this.globalPrompts = prompts),
			takeUntil(this.destroy$)
		).subscribe();

		this.aiAssistantStore.prompts$.pipe(
			map((prompts) => this.localPrompts = prompts),
			takeUntil(this.destroy$)
		).subscribe();

		this.aiAssistantStore.currentPrompt$.pipe(
			map((current) => this.selectedPrompt = current),
			tap((prompt) => {
				if (prompt) {
					this.promptControl.setValue(prompt.title);
					this.promptControl.updateValueAndValidity();
					this.showPromptControl = false;
				}
				else {
					this.promptControl.setValue('');
				}
				this.reloadPrompts();
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.aiAssistantStore.currentDocIds$.pipe(
			map((id: string[]) => this.currentDocIds = id),
			takeUntil(this.destroy$)
		).subscribe();

		this.aiAssistantStore.hasMoreResponse$.pipe(
			map((v) => this.showContinue = v),
			takeUntil(this.destroy$)
		).subscribe();

		this.aiAssistantStore.currentWorkflowKey$.pipe(
			map((workflowKey) => {
				this.currentWorkflowKey = workflowKey;
				if (workflowKey)
					this.aiAssistantStore.scheduleStreaming({ waitInMs: 5000, autoScroll: this.autoScroll.bind(this) });
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.status$.pipe(
			map((status) => {
				this.showRetry = false;
				switch (status) {
					case 'initializing':
						this.setLoadingStatus(false);
						break;
					case 'waiting':
						this.autoScroll();
						this.setLoadingStatus(false);
						break;
					case 'prompting':
						this.mode = 'query';
						this.setLoadingStatus(true);
						break;
					case 'responding':
						this.mode = 'indeterminate';
						this.shouldAutoScroll = true;
						this.setLoadingStatus(true);
						break;
					case 'continuing':
						this.isResultLoading = true;
						this.setLoadingStatus(true);
						break;
					case 'error':
						this.showRetry = true;
						this.setLoadingStatus(false);
						break;
				}
			}),
			takeUntil(this.destroy$)).subscribe();

		this.promptControl.valueChanges.pipe(
			distinctUntilChanged(),
			map((data: string | any) => {
				this.reloadPrompts();
				if (data && typeof (data) === 'string') {
					if (!this.selectedPrompt || data !== this.selectedPrompt.title) {
						if (data.length < 256)
							this.filterLocalPrompts(data);
						else
							this.localPrompts = [];
						this.showTokenWarning = this.aiAssistantStore.displayTokenLimitWarning(this.currentModel, data);
						this.showTokenError = this.aiAssistantStore.displayTokenLimitError(this.currentModel, data);
					}
				}
				else if (data && typeof (data) !== 'string') {
					this.filterLocalPrompts(data.title);
					this.aiAssistantStore.setPrompt(data);
					this.showTokenWarning = false;
					this.showTokenError = false;
				}
				else {
					this.reloadPrompts();
					this.aiAssistantStore.setPrompt(null);
					this.showRetry = false;
					this.showContinue = false;
					this.showTokenWarning = false;
					this.showTokenError = false;
				}
			}),
			takeUntil(this.destroy$)
		).subscribe();
	}

	ngOnInit() {
		this.aiAssistantStore.initialize(this.selectedAiEngineModel);
		if (this.selectionService.selection.selected.length === this.selectionService.SelectedCount) {
			this.aiAssistantStore.setDocIds(this.selectionService.selection.selected.map(x => x.docId));
		}
		else {
			let i = 0;
			const pageSize = 100;
			this.loadSelectedDocs(i, pageSize)
				.pipe(expand(() => {
					if (this.selectedDocIds.length < this.selectionService.SelectedCount) {
						i += 1;
						return this.loadSelectedDocs(i, pageSize);
					}
					return of(null);
				}))
				.subscribe((data) => {
					if (!data)
						this.aiAssistantStore.setDocIds(this.selectedDocIds);
				});
		}


	}

	override ngOnDestroy(): void {
		this.aiAssistantStore.setPrompt(null);
		if (this.isLoading)
			this.cancelRequest();
		this.aiAssistantStore.setDocIds(null);
		super.ngOnDestroy();
	}


	public onScroll(event): void {
		this.isResultLoading = true;
		if (Math.abs(event.srcElement.scrollHeight - event.srcElement.scrollTop - event.srcElement.clientHeight) < 2)
			this.shouldAutoScroll = true;
		else
			this.shouldAutoScroll = false;

	}

	public getResponseText(responseArray: string[]): string {
		return responseArray.join('\n\n');
	}

	public displayFn(prompt: Prompt): string {
		return prompt?.fullPrompt || '';
	}

	public enterPrompt(): void {
		const data = this.promptControl.value;
		if (typeof (data) === 'string') {
			if (this.promptControl.value.length === 0 || this.showTokenError)
				return;
			this.aiAssistantStore.createAndPrompt(this.promptControl.value);
			return;
		}
		this.showPromptControl = false;
		this.aiAssistantStore.answerPrompt();
	}

	public togglePromptControl(): void {
		if (!this.isLoading) {
			this.showPromptControl = true;
			if (this.selectedPrompt) {
				this.promptControl.setValue(this.selectedPrompt);
			}
		}
	}

	public clearPrompt(): void {
		this.promptControl.setValue('');
		this.showPromptControl = true;
	}

	public cancelRequest(): void {
		this.aiAssistantStore.cancelRequest();
	}

	public deletePrompt(promptId: string) {
		this.aiAssistantStore.deletePrompt(promptId);
	}

	public promptSettings(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.width = '900px';
		dialogConfig.maxHeight = '480px';
		dialogConfig.autoFocus = false;
		dialogConfig.data = this.selectedPrompt;
		const dialogObservable = this.dialog.open(CustomAiPromptComponent, dialogConfig).afterClosed();

		dialogObservable.pipe(
			tap(() => {
				if (this.selectedPrompt) {
					this.aiAssistantStore.refreshCurrentPrompt();
				}
				else {
					this.reloadPrompts();
				}
			})).subscribe();
	}

	public changeAiEngine(): void {
		this.aiAssistantStore.setExternalAppId(this.selectedAiEngineModel);
		this.promptControl.setValue('');
		this.resetAll();
	}

	public copyResponseToClipboard(): void {
		this.aiAssistantStore.copyResponseToClipboard();
	}

	public selectPromptChange(event: MatAutocompleteSelectedEvent): void {
		this.showPromptControl = true;
		this.promptControl.setValue(event.option.value);
	}

	public goToNextChunk(): void {
		this.aiAssistantStore.getNextChunk()
	}

	public retry(): void {
		this.aiAssistantStore.retryPrompt();
	}

	public close(): void {
		this.dialogRef.close();
	}

	public showClearButton(): boolean {
		const promptValue = this.promptControl.value;
		if (promptValue && typeof (promptValue) === 'string')
			return !this.isLoading && promptValue?.length > 0;
		if (promptValue)
			return !this.isLoading;
		return false;
	}

	private resetAll() {
		this.showRetry = false;
		this.showContinue = false;
	}

	private reloadPrompts(): void {
		this.aiAssistantStore.clearPrompts();
		this.aiAssistantStore.addPrompts(this.globalPrompts);
	}

	private filterLocalPrompts(term: string = ''): void {
		this.localPrompts = this.localPrompts.filter(p =>
			p.title.toLocaleLowerCase().includes(term.toLocaleLowerCase())
			|| p.fullPrompt.toLocaleLowerCase().includes(term.toLocaleLowerCase()));
	}

	private setLoadingStatus(isLoading: boolean) {
		if (isLoading) {
			this.isLoading = true;
			this.promptControl.disable();
		}
		else {
			this.isLoading = false;
			this.isResultLoading = false;
			this.promptControl.enable();
		}
	}

	private loadSelectedDocs(currentPageIndex: number, pageSize: number): Observable<any> {
		return this.docService.getDocs(currentPageIndex, pageSize, this.sortBy, this.selectionService.getSavedFilterId(), false, false, false, false, this.expandEmailFamilySelected, this.searchParameter.searchString?.length > 0,
			this.searchParameter.searchString, false)
			.pipe(map((data) =>
				this.selectedDocIds = this.selectedDocIds.concat(data.docs.map(x => x.docId))
			));

	}

	private autoScroll(): void {
		if (this.shouldAutoScroll) {
			try {
				setTimeout(() => {
					if (this.scrollContainer?.nativeElement)
						this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight
				});
				// eslint-disable-next-line
			} catch (err) { }
		}
	}
}
